/**
 * NotificationBell — sino na navbar com dropdown estilo Facebook.
 * Clica → abre painel flutuante com últimas notificações + permissão push.
 */
import { useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, BellOff, ShieldCheck, AlertCircle } from 'lucide-react';
import { NotificationsAPI, NotificationItem } from './api';
import { worldName } from './worldNames';
import { useItemNames } from './itemNames';
import { getVapidPublicKey, pushSubscribe, pushUnsubscribe } from '@/shared/services/pushService';
import { useAuth } from '@/shared/contexts/AuthContext';

/**
 * Faz parse da mensagem de loot_announce e devolve partes estruturadas pra render
 * com estilos diferentes (nome do char/item/mundo em destaque).
 *
 * Pattern do server EventAlert.cpp:159:
 *   "%s encontrou um item raro (kind=0x%llX opt=0x%llX) em World %d[ no Canal %d]"
 *
 * `kind` no server e' o ItemKindIdx 64-bit CRU (sem mask). A tabela
 * `cabal_item_list` indexa por ItemKind cru (ex 33557340 = 0x2000B5C =
 * "Joia Azul Enfraquecida"), entao NAO mascaramos antes do lookup.
 *
 * Suporta opcionalmente "no Canal N" no fim — proxima versao da .so vai
 * incluir esse sufixo. Enquanto nao chega, o front aceita ambos os formatos.
 */
interface LootParts {
  who: string;
  itemId: number;        // ItemKind cru pra cruzar com cabal_item_list
  optInt: number;        // opt decimal (so' mostra se != 0)
  worldName: string;
  channel: number | null;
}

function parseLootAnnounce(message: string): LootParts | null {
  const m = message.match(
    /^(.+?) encontrou um item raro \(kind=0x([0-9A-Fa-f]+)\s+opt=0x([0-9A-Fa-f]+)\) em World (\d+)(?:\s+no Canal (\d+))?$/,
  );
  if (!m) return null;
  const [, who, kindHex, optHex, worldId, channelStr] = m;
  // BigInt pra suportar 64-bit; depois converte sem mask
  let itemId: number;
  try {
    itemId = Number(BigInt('0x' + kindHex));
  } catch {
    itemId = parseInt(kindHex, 16);
  }
  const optInt = parseInt(optHex, 16) || 0;
  return {
    who,
    itemId,
    optInt,
    worldName: worldName(parseInt(worldId, 10)),
    channel: channelStr ? parseInt(channelStr, 10) : null,
  };
}

const POLL_INTERVAL_MS = 30_000;
const PAGE_SIZE = 15;

function fmtTime(iso: string) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('pt-BR');
}

function kindLabel(kind: string) {
  const map: Record<string, string> = {
    broadcast: 'Sistema',
    char_dm: 'Mensagem',
    push_user: 'Conta',
    loot_announce: 'Loot',
  };
  return map[kind] || kind;
}

function kindAccent(kind: string) {
  switch (kind) {
    case 'push_user': return 'border-l-red-500';
    case 'loot_announce': return 'border-l-yellow-500';
    case 'broadcast': return 'border-l-blue-500';
    default: return 'border-l-gray-500';
  }
}

interface Props {
  unreadHint?: number;
  /** 'liberty' = gatilho no visual novo do painel do jogador (.bell do liberty.css). */
  variant?: 'default' | 'liberty';
}

function base64UrlToUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function NotificationBell({ variant = 'default' }: Props = {}) {
  const { account } = useAuth();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pushLoading, setPushLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  // Badge "congelado" com o valor de abertura — segue visivel enquanto o popup
  // fica aberto (mesmo apos zerar em background).
  const [openedBadge, setOpenedBadge] = useState(0);
  const containerRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const supported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
  const browserPerm: NotificationPermission = supported && 'Notification' in window
    ? Notification.permission : 'default';

  // Checa subscription existente
  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub);
      } catch {}
    })();
  }, [supported]);

  async function subscribe() {
    setPushLoading(true);
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        alert('Seu navegador nao suporta notificacoes push. Tente Chrome/Edge/Firefox.\nNo iPhone, instale o site como app (Compartilhar -> Adicionar a Tela de Inicio).');
        return;
      }

      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();
      if (perm === 'denied') {
        alert('Notificacoes bloqueadas pelo navegador. Desbloqueie no icone de cadeado da barra de endereco.');
        return;
      }
      if (perm !== 'granted') return;

      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const { publicKey } = await getVapidPublicKey();
      if (!publicKey) {
        alert('Backend nao retornou chave VAPID. Tente novamente.');
        return;
      }
      const appServerKey = base64UrlToUint8Array(publicKey);

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: appServerKey.buffer as ArrayBuffer,
        });
      }
      await pushSubscribe({
        userNum: account?.userNum ?? null,
        charIdx: null,
        kind: 0,
        userAgent: navigator.userAgent.slice(0, 256),
        subscription: sub.toJSON() as PushSubscriptionJSON,
      });
      setIsSubscribed(true);
    } catch (err: any) {
      console.error('[push] subscribe falhou:', err);
      alert('Falha ao ativar notificacoes: ' + (err?.message || err));
    } finally {
      setPushLoading(false);
    }
  }

  async function unsubscribe() {
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await pushUnsubscribe(sub.endpoint);
          await sub.unsubscribe();
        }
      }
      setIsSubscribed(false);
    } finally {
      setPushLoading(false);
    }
  }

  // Poll do contador
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await NotificationsAPI.myUnreadCount();
        if (alive) setUnread(r.unread);
      } catch {}
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Carrega primeira pagina ao abrir
  async function loadList(p = 1, append = false) {
    setLoading(true);
    try {
      const r = await NotificationsAPI.myList(p, PAGE_SIZE, false);
      setItems(append ? [...items, ...r.items] : r.items);
      setUnread(r.unread);
      setTotal(r.total);
      setHasMore(p * PAGE_SIZE < r.total);
      setPage(p);
    } finally {
      setLoading(false);
    }
  }

  // Scroll infinito: dispara loadMore ao chegar perto do bottom
  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    if (loading || !hasMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadList(page + 1, true);
    }
  }

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      setOpenedBadge(unread);        // congela o badge no valor de abertura
      loadList(1, false);
      // UX Facebook-style: abrir o popup ja zera o badge.
      // Marca tudo lido em background (sem aguardar resposta) e atualiza items
      // locais pra UI ficar consistente (sem bolinha azul nem bg destacado).
      if (unread > 0) {
        const nowIso = new Date().toISOString();
        setUnread(0);
        setItems(prev => prev.map(i => i.readAt ? i : { ...i, readAt: nowIso }));
        NotificationsAPI.markAllRead().catch(() => {
          // se falhar, o proximo poll restaura o contador real
        });
      }
    }
  }

  async function handleRead(n: NotificationItem) {
    if (n.readAt) return;
    try {
      await NotificationsAPI.markRead(n.id);
      setItems(items.map(i => i.id === n.id ? { ...i, readAt: new Date().toISOString() } : i));
      setUnread(Math.max(0, unread - 1));
    } catch {}
  }

  async function handleReadAll() {
    await NotificationsAPI.markAllRead();
    setItems(items.map(i => i.readAt ? i : { ...i, readAt: new Date().toISOString() }));
    setUnread(0);
  }

  // ── Sub-render: card de permissão ──
  const renderPermissionCard = () => {
    if (!supported) {
      return (
        <div className="px-3 py-2 border-b border-white/10 bg-yellow-500/5 text-xs flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="text-white/70">
            Seu navegador não suporta notificações. Use Chrome/Edge/Firefox.
          </div>
        </div>
      );
    }
    if (browserPerm === 'denied') {
      return (
        <div className="px-3 py-2 border-b border-white/10 bg-red-500/5 text-xs flex items-start gap-2">
          <BellOff className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="text-white/70">
            Notificações bloqueadas. Desbloqueie no ícone de cadeado da barra de endereço.
          </div>
        </div>
      );
    }
    if (isSubscribed && browserPerm === 'granted') {
      return (
        <div className="px-3 py-2 border-b border-white/10 bg-green-500/5 text-xs flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span className="text-white/70">Notificações ativadas</span>
          </div>
          <button
            onClick={unsubscribe}
            disabled={pushLoading}
            className="px-2 py-0.5 bg-red-600/70 hover:bg-red-600 text-white rounded text-[10px] font-semibold disabled:opacity-50 cursor-pointer"
          >
            Desativar
          </button>
        </div>
      );
    }
    return (
      <div className="px-3 py-2 border-b border-white/10 bg-blue-500/5 flex items-center justify-between gap-2">
        <div className="text-xs text-white/75">Ative pra receber alertas mesmo com a aba fechada</div>
        <button
          onClick={subscribe}
          disabled={pushLoading}
          className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold disabled:opacity-50"
        >
          {pushLoading ? '...' : 'Ativar'}
        </button>
      </div>
    );
  };

  // Coleta IDs de items dos loots visiveis e resolve nomes via /api/items/names.
  // O hook agrupa em 1 batch (50ms debounce) e cacheia em memoria pra sessao.
  const lootIds: number[] = [];
  for (const n of items) {
    if (n.kind === 'loot_announce') {
      const p = parseLootAnnounce(n.message);
      if (p) lootIds.push(p.itemId);
    }
  }
  const itemNames = useItemNames(lootIds);

  // Enquanto aberto, segura o badge no valor da abertura (openedBadge).
  const badge = open ? openedBadge : unread;
  const badgeText = badge > 99 ? '99+' : String(badge);
  // Liberty: wrapper <span> — o CSS do painel novo estiliza `.panel-user > div`
  // e esconderia/deformaria um <div> aqui.
  const Wrapper = variant === 'liberty' ? 'span' : 'div';

  return (
    <Wrapper className="relative" ref={containerRef as React.RefObject<HTMLDivElement & HTMLSpanElement>}>
      {variant === 'liberty' ? (
        <button onClick={toggle} className="bell" aria-label="Notificações" title="Notificações">
          ♙{badge > 0 && <b>{badgeText}</b>}
        </button>
      ) : (
      <button
        onClick={toggle}
        className={`relative inline-flex items-center justify-center w-10 h-10 rounded-full transition cursor-pointer ${open ? 'bg-white/15 ring-1 ring-white/25' : 'hover:bg-white/10'}`}
        aria-label="Notificações"
        title="Notificações"
      >
        <Bell className="w-5 h-5 text-white/85" />
        {badge > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {badgeText}
          </span>
        ) : null}
      </button>
      )}

      {open && (
        <>
          {/* Backdrop escurecido (desktop + mobile) */}
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[199]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[360px] max-w-[95vw] bg-[#161e26] border border-white/10
                          rounded-lg shadow-2xl overflow-hidden z-[200]">
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-[#0f1418]">
            <div className="font-semibold text-white">Notificações</div>
            {unread > 0 && (
              <button
                onClick={handleReadAll}
                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 cursor-pointer"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Marcar todas
              </button>
            )}
          </div>

          {/* Permission card */}
          {renderPermissionCard()}

          {/* Lista com scroll infinito */}
          <div ref={scrollRef} onScroll={onScroll} className="max-h-[420px] overflow-y-auto">
            {items.length === 0 && !loading && (
              <div className="px-4 py-8 text-center text-sm text-white/55">
                Nenhuma notificação no momento.
              </div>
            )}
            {items.map(n => (
              <button
                key={n.id}
                onClick={() => handleRead(n)}
                className={`w-full text-left px-3 py-2.5 border-l-4 ${kindAccent(n.kind)}
                            ${n.readAt
                              ? ''
                              : 'bg-blue-500/[0.06] shadow-[inset_3px_0_0_rgba(59,130,246,0.6)]'}
                            hover:bg-white/[0.04] transition border-b border-white/5
                            flex items-start justify-between gap-2 cursor-pointer`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/45">{kindLabel(n.kind)}</span>
                    {n.title && <span className="text-xs font-semibold text-white truncate">{n.title}</span>}
                    {!n.readAt && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></span>}
                  </div>
                  <div className="text-xs text-white/55 line-clamp-2">
                    {(() => {
                      if (n.kind === 'loot_announce') {
                        const p = parseLootAnnounce(n.message);
                        if (p) {
                          const name = itemNames.get(p.itemId);
                          // name === null: pendente fetch OU id sem nome no DB
                          const itemLabel = name ?? `item #${p.itemId}`;
                          // opt=quantidade no drop. >=1 prefixa "Nx". 0 = sem prefixo.
                          const qty = p.optInt > 0 ? p.optInt : null;
                          return (
                            <>
                              <span className="text-white/70">{p.who}</span>
                              <span> encontrou </span>
                              {qty != null && (
                                <span className="text-amber-300 font-bold">{qty}x </span>
                              )}
                              <span className="text-yellow-300 font-semibold">{itemLabel}</span>
                              <span> em </span>
                              <span className="text-cyan-300 font-semibold">{p.worldName}</span>
                              {p.channel != null && (
                                <span className="text-white/55"> · canal {p.channel}</span>
                              )}
                            </>
                          );
                        }
                      }
                      return <span>{n.message}</span>;
                    })()}
                  </div>
                </div>
                <div className="text-[10px] text-white/40 whitespace-nowrap flex-shrink-0">{fmtTime(n.createdAt)}</div>
              </button>
            ))}
            {loading && (
              <div className="px-4 py-3 text-center text-xs text-white/55">Carregando…</div>
            )}
            {!hasMore && items.length > 0 && !loading && (
              <div className="px-4 py-3 text-center text-[10px] text-white/30">— fim ({total} total) —</div>
            )}
          </div>
        </div>
        </>
      )}
    </Wrapper>
  );
}
